import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ArrowLeft,
  Crown,
  Search,
  FileText,
  Users,
  UserCheck,
  Filter,
  User,
  ChevronDown,
  LogOut,
  Building2,
  ExternalLink
} from 'lucide-react';

// Using relative URLs

const SuperAdminAllAudits = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [audits, setAudits] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [auditorFilter, setAuditorFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');

  useEffect(() => {
    fetchAllAudits();
    fetchAllAuditors();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAllAudits = async () => {
    try {
      const response = await axios.get(`/api/admin/all-audits`, getAuthHeaders());
      setAudits(response.data.audits);
      setCustomers(response.data.customers || []);
    } catch (error) {
      toast.error('Failed to load audits');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAuditors = async () => {
    try {
      const response = await axios.get(`/api/admin/auditors`, getAuthHeaders());
      setAuditors(response.data.auditors);
    } catch (error) {
      console.error('Failed to load auditors:', error);
    }
  };

  const handleAssignAuditor = async (auditId, auditorId) => {
    try {
      await axios.post(
        `/api/audits/${auditId}/assign-auditor?auditor_id=${auditorId}`,
        {},
        getAuthHeaders()
      );
      toast.success('Auditor assigned successfully!');
      fetchAllAudits();
    } catch (error) {
      toast.error('Failed to assign auditor');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      under_review: 'bg-yellow-100 text-yellow-700',
      certified: 'bg-emerald-100 text-navy-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = 
      audit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      audit.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      audit.owner_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    const matchesAuditor = auditorFilter === 'all' || audit.assigned_auditor_id === auditorFilter;
    const matchesCustomer = customerFilter === 'all' || audit.organization_id === customerFilter;
    
    return matchesSearch && matchesStatus && matchesAuditor && matchesCustomer;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div>
                <h1 className="text-2xl font-bold text-slate-900">All Audits</h1>
                <p className="text-sm text-slate-600">Platform-wide audit management</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Audits</CardDescription>
              <CardTitle className="text-3xl">{audits.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>In Progress</CardDescription>
              <CardTitle className="text-3xl">
                {audits.filter(a => a.status === 'in_progress').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Assigned Auditors</CardDescription>
              <CardTitle className="text-3xl">
                {audits.filter(a => a.assigned_auditor_id).length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Auditors</CardDescription>
              <CardTitle className="text-3xl">{auditors.length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search audits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="certified">Certified</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={auditorFilter} onValueChange={setAuditorFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by auditor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Auditors</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {auditors.map(auditor => (
                    <SelectItem key={auditor.id} value={auditor.id}>
                      {auditor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Audits Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Platform Audits</CardTitle>
            <CardDescription>
              {filteredAudits.length} audit{filteredAudits.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredAudits.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No audits found
                </div>
              ) : (
                filteredAudits.map((audit) => (
                  <div key={audit.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="grid grid-cols-12 gap-4">
                      {/* Audit Info - 3 cols */}
                      <div className="col-span-3">
                        <p className="font-semibold text-slate-900 mb-1">{audit.name}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Building2 className="w-3 h-3" />
                          <span>{audit.organization_name}</span>
                        </div>
                      </div>
                      
                      {/* Frameworks & Status - 3 cols */}
                      <div className="col-span-3">
                        <div className="flex flex-wrap gap-1 mb-2">
                          {audit.frameworks?.slice(0, 2).map((fw, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                          {audit.frameworks?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{audit.frameworks.length - 2}
                            </Badge>
                          )}
                        </div>
                        <Badge className={getStatusColor(audit.status)}>
                          {audit.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      {/* Progress - 2 cols */}
                      <div className="col-span-2 flex items-center">
                        <div className="w-full">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-600">Progress</span>
                            <span className="text-xs font-medium text-slate-900">{audit.progress}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-navy-600 h-2 rounded-full transition-all"
                              style={{width: `${audit.progress}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Auditor Assignment - 2 cols */}
                      <div className="col-span-2">
                        {audit.assigned_auditor_email ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <UserCheck className="w-3 h-3" />
                              <span>Assigned Auditor:</span>
                            </div>
                            <p className="text-sm text-slate-900 font-medium truncate">
                              {audit.assigned_auditor_name || audit.assigned_auditor_email}
                            </p>
                            <Select 
                              value=""
                              onValueChange={(value) => handleAssignAuditor(audit.id, value)}
                            >
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder="Reassign" />
                              </SelectTrigger>
                              <SelectContent>
                                {auditors.map(auditor => (
                                  <SelectItem key={auditor.id} value={auditor.id}>
                                    {auditor.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-600 mb-1">Assign Auditor:</p>
                            <Select 
                              value={audit.assigned_auditor_id || ''}
                              onValueChange={(value) => handleAssignAuditor(audit.id, value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select auditor" />
                              </SelectTrigger>
                              <SelectContent>
                                {auditors.map(auditor => (
                                  <SelectItem key={auditor.id} value={auditor.id}>
                                    {auditor.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions - 2 cols */}
                      <div className="col-span-2 flex items-center justify-end">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/audit/${audit.id}`)}
                          className="bg-navy-600 hover:bg-navy-700 text-white"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Access Audit
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminAllAudits;
